import * as React from "react";

import { HttpErrorDto } from "@common/dto/http";
import { trDts } from "@modules/datasheets/utils/i18n";
import {
  GetDatasheetFolderFormats,
  loadDSMostRecent,
  saveLastUsedFormat,
} from "@modules/datasheets/services/formats";

import { FormatFieldItem } from "@common/components/format-field-selector";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";

export type DatasheetFormatManagementFolder = Partial<FolderPrimaryListDto>;

const toFormatFieldItem = (item: { id: number; name: string; folderid: number }): FormatFieldItem => ({
  id: item.id,
  name: item.name,
  folderId: item.folderid,
  description: "",
  imageURL: "gif/format.gif",
});

export type DatasheetFormatManagementProps = {
  getFolders: () => Promise<DatasheetFormatManagementFolder[]>;
  getFormats: (params: GetDatasheetFolderFormats) => Promise<Array<FormatFieldItem>>;
  onSubmit: (params: { format: FormatFieldItem; folder: DatasheetFormatManagementFolder }) => void;
  showErrorMessage: (message: string) => void;
  /** Context for load/save MRU: 1 = IDS, 2 = PDS. Omit for non-datasheet pickers. */
  context?: number;
  /** When true, load recently used formats. Pass from parent when picker opens. */
  pickerOpened?: boolean;
  /** Current format loaded for the pedigree/individual. Shown on the "current" line (GWT-style). */
  initialSelectedFormat?: FormatFieldItem | { id: number; name: string; folderId?: number };
  /** When this changes (e.g. pedigree reopened), reload recently used formats from backend */
  reloadRecentlyUsedKey?: number | string;
};

export const toFormatFieldItemOrUndefined = (
  v?: FormatFieldItem | { id: number; name: string; folderId?: number }
): FormatFieldItem | undefined => {
  if (!v?.id || !v?.name) return undefined;
  return {
    id: v.id,
    name: v.name,
    folderId: (v as FormatFieldItem).folderId ?? (v as { folderId?: number }).folderId ?? 0,
    description: (v as FormatFieldItem).description ?? "",
    imageURL: (v as FormatFieldItem).imageURL ?? "gif/format.gif",
  };
};

export const useDatasheetFormatManagementController = ({
  onSubmit,
  getFolders,
  getFormats,
  showErrorMessage,
  context = 1,
  pickerOpened = false,
  initialSelectedFormat,
  reloadRecentlyUsedKey,
}: DatasheetFormatManagementProps) => {
  const [folders, setFolders] = React.useState<Array<DatasheetFormatManagementFolder>>([]);
  const [folderFormats, setFolderFormats] = React.useState<Array<FormatFieldItem>>([]);

  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [isFolderFormatsLoading, setIsFolderFormatsLoading] = React.useState(false);

  const [selectedFolder, setSelectedFolder] = React.useState<DatasheetFormatManagementFolder | undefined>();
  const [selectedFormat, setSelectedFormat] = React.useState<FormatFieldItem | undefined>();

  const [recentlyUsedFormats, setRecentlyUsedFormats] = React.useState<Array<FormatFieldItem>>([]);
  const [sessionFormats, setSessionFormats] = React.useState<Array<FormatFieldItem>>([]);
  const [isRecentlyUsedLoading, setIsRecentlyUsedLoading] = React.useState(false);

  const handleGetFolders = React.useCallback(async () => {
    try {
      setIsFoldersLoading(true);
      const folders = await getFolders();
      setFolders(folders);
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || trDts("Failed to get folders");
      showErrorMessage(errorMessage);
    } finally {
      setIsFoldersLoading(false);
    }
  }, [showErrorMessage, getFolders]);

  const handleGetFormats = React.useCallback(
    async (params: GetDatasheetFolderFormats) => {
      try {
        setIsFolderFormatsLoading(true);
        const folderFormats = await getFormats(params);
        setFolderFormats(folderFormats);
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || trDts("Failed to get formats");
        showErrorMessage(errorMessage);
      } finally {
        setIsFolderFormatsLoading(false);
      }
    },
    [showErrorMessage, getFormats]
  );

  const handleSelectedFolderChange = React.useCallback(
    (folder: DatasheetFormatManagementFolder) => {
      setSelectedFolder(folder);
      // Get context from folder, but if it's 0 or missing, it will be handled by getDatasheetFolderFormats
      // which will use props.context as fallback
      const folderContext = (folder)?.context ?? (folder)?.fieldcontext ?? 0;
      handleGetFormats({
        folderId: folder.id!,
        context: folderContext,
      });
    },
    [handleGetFormats]
  );

  const handleLoadRecentlyUsed = React.useCallback(async () => {
    try {
      setIsRecentlyUsedLoading(true);
      const list = await loadDSMostRecent(context);
      setRecentlyUsedFormats(list.map(toFormatFieldItem));
    } catch (err) {
      console.error(err);
    } finally {
      setIsRecentlyUsedLoading(false);
    }
  }, [context]);

  const handleFormatSelect = React.useCallback(
    (format: FormatFieldItem) => {
      setSelectedFormat(format);

      setSessionFormats((prev) => {
        const exists = prev.some((f) => Number(f.id) === Number(format.id));
        if (exists) return prev;
        const inRecentlyUsed = recentlyUsedFormats.some((f) => Number(f.id) === Number(format.id));
        if (inRecentlyUsed) return prev;
        return [...prev, format];
      });

      saveLastUsedFormat(Number(format.id), context).catch((err) => {
        console.error("Failed to save last used format", err);
      });

      const folder = selectedFolder ?? {
        id: format.folderId,
        context,
        fieldcontext: context,
      } as DatasheetFormatManagementFolder;
      onSubmit({ folder, format });
    },
    [onSubmit, selectedFolder, context, recentlyUsedFormats]
  );

  // Sync selectedFormat when parent passes the loaded format (e.g. pedigree opened with format)
  React.useEffect(() => {
    const resolved = toFormatFieldItemOrUndefined(initialSelectedFormat);
    if (resolved) {
      setSelectedFormat(resolved);
    }
  }, [initialSelectedFormat?.id, initialSelectedFormat?.name]);

  React.useEffect(() => {
    handleGetFolders();
  }, []);

  // Load only on mount and when pedigree is reopened (reloadRecentlyUsedKey changes).
  // Do NOT reload when picker opens - top section must stay static during current session.
  React.useEffect(() => {
    handleLoadRecentlyUsed();
  }, [handleLoadRecentlyUsed, reloadRecentlyUsedKey]);

  return {
    folders,
    folderFormats,
    isFoldersLoading,
    isFolderFormatsLoading,
    selectedFolder,
    selectedFormat,
    handleSelectedFolderChange,
    handleFormatSelect,
    recentlyUsedFormats,
    sessionFormats,
    isRecentlyUsedLoading,
  };
};
